/**
 * <CallbackPopup> — modal de demande de rappel.
 *
 * Déclenchable depuis n'importe où via le hook useCallbackPopup ou en bindant
 * le bouton du Header. Le formulaire est court (nom + tel + créneau + sujet)
 * pour maximiser la conversion.
 */
import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone, Lock, CheckCircle2 } from "lucide-react";
import { useSiteContent } from "@/lib/useSiteContent";
import { keysFor } from "@/config/cmsSections";


const TIME_OPTIONS = [
    { value: "anytime",   label: "N'importe quand" },
    { value: "morning",   label: "Matin (9h – 12h)" },
    { value: "afternoon", label: "Après-midi (14h – 18h)" },
    { value: "evening",   label: "Soir (18h – 20h)" },
];


export default function CallbackPopup({ open, onOpenChange }) {
    const c = useSiteContent(keysFor("callback"));
    const [form, setForm] = useState({ full_name: "", phone: "", best_time: "anytime", topic: "" });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/callback-requests", form);
            setSent(true);
            toast.success("Demande envoyée");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setForm({ full_name: "", phone: "", best_time: "anytime", topic: "" });
        setSent(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setTimeout(reset, 300); }}>
            <DialogContent className="max-w-md rounded-3xl">
                <DialogHeader>
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-accent flex items-center justify-center">
                            <Phone className="h-5 w-5 text-accent-foreground" />
                        </div>
                        <DialogTitle className="font-display text-2xl font-bold">
                            {c("callback.title", "Demande de rappel")}
                        </DialogTitle>
                    </div>
                    <DialogDescription className="pt-2">
                        {c("callback.subtitle", "Laissez-nous votre numéro, un conseiller vous rappelle sous 24h ouvrées.")}
                    </DialogDescription>
                </DialogHeader>

                {sent ? (
                    <div className="text-center py-8">
                        <CheckCircle2 className="h-14 w-14 mx-auto text-primary" />
                        <p className="text-muted-foreground mt-4 leading-relaxed">
                            {c("callback.success", "Merci ! Un conseiller vous rappelle dans les meilleurs délais.")}
                        </p>
                        <Button onClick={() => onOpenChange(false)} className="rounded-full mt-6 bg-primary hover:bg-primary/90">
                            Fermer
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4 pt-2">
                        <div>
                            <Label className="em-eyebrow">{c("callback.label_name", "Votre nom")} *</Label>
                            <Input
                                required
                                value={form.full_name}
                                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                className="rounded-xl mt-2"
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label className="em-eyebrow">{c("callback.label_phone", "Numéro de téléphone")} *</Label>
                            <Input
                                required
                                type="tel"
                                value={form.phone}
                                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                className="rounded-xl mt-2"
                                placeholder="+33 6 12 34 56 78"
                            />
                        </div>
                        <div>
                            <Label className="em-eyebrow">{c("callback.label_time", "Quand vous rappeler ?")}</Label>
                            <Select value={form.best_time} onValueChange={(v) => setForm({ ...form, best_time: v })}>
                                <SelectTrigger className="rounded-xl mt-2"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {TIME_OPTIONS.map((o) => (
                                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="em-eyebrow">{c("callback.label_topic", "Sujet (optionnel)")}</Label>
                            <Input
                                value={form.topic}
                                onChange={(e) => setForm({ ...form, topic: e.target.value })}
                                className="rounded-xl mt-2"
                                placeholder="Devis B2B, isolation, solaire…"
                            />
                        </div>
                        <p className="text-xs text-muted-foreground flex items-start gap-2 pt-1">
                            <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                            Vos coordonnées sont protégées (RGPD) et utilisées uniquement pour vous rappeler.
                        </p>
                        <Button type="submit" disabled={submitting} className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold h-11">
                            <Phone className="h-4 w-4 mr-2" />
                            {submitting ? "Envoi…" : c("callback.submit", "Être rappelé")}
                        </Button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
